/**
 * AdLevelsWidget — «Реклама по уровням»
 * =======================================
 *
 * Сколько рекламы смотрят игроки на каждом уровне кампании и в каких группах
 * плейсментов. Диапазон дат дашборда — окно РЕГИСТРАЦИЙ (когорта), в отличие
 * от остальных вкладок «Рекламы»: см. server/src/services/ads/adLevelsService.ts.
 *
 * Сервер-зависимые параметры (гео, макс. уровень, макс. день жизни) уходят в
 * запрос; режим ячеек, градиент, порядок колонок и сортировка строк режутся на
 * клиенте без перезапроса. Платформа и версии клиента — общие фильтры раздела.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { AdsService } from '../../../services/adsService';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { AdLevelsTable } from './adLevels/AdLevelsTable';
import { CommitInput, parseBounded } from './adLevels/CommitInput';
import { LEVEL_SORT_KEY, METRIC_COLUMNS, MODE_IMPS, MODE_PCT, buildPlacementColumns } from './adLevels/model';

const DEFAULT_MAX_LEVEL = 50;
const LEVEL_LIMIT = 1000;
const DAY_LIMIT = 3650;

export function AdLevelsWidget() {
  const [maxLevel, setMaxLevel] = useLocalStorage('ads-levels:max-level', DEFAULT_MAX_LEVEL);
  const [maxDay, setMaxDay] = useLocalStorage('ads-levels:max-day', null);
  const [mode, setMode] = useLocalStorage('ads-levels:mode', MODE_IMPS);
  const [gradient, setGradient] = useLocalStorage('ads-levels:gradient', true);
  const [orderByTotal, setOrderByTotal] = useLocalStorage('ads-levels:order-by-total', true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [sortKey, setSortKey] = useState(LEVEL_SORT_KEY);
  const [sortDir, setSortDir] = useState('asc');

  const levelParam = parseBounded(maxLevel, 1, LEVEL_LIMIT) ?? DEFAULT_MAX_LEVEL;
  const dayParam = parseBounded(maxDay, 0, DAY_LIMIT);

  const levelsData = useAdsWidgetData(
    async (filters, options) =>
      AdsService.get(
        '/levels',
        filters,
        {
          maxLevel: levelParam,
          maxDay: dayParam ?? undefined
        },
        options
      ),
    [levelParam, dayParam]
  );
  const widgetProps = useBaseWidget(levelsData);
  const { data } = widgetProps;

  const placementColumns = useMemo(
    () => buildPlacementColumns(data?.groups, expanded, orderByTotal),
    [data?.groups, expanded, orderByTotal]
  );
  const expandableIds = useMemo(
    () => (data?.groups || []).filter((g) => (g.details || []).length > 1).map((g) => g.id),
    [data?.groups]
  );
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));

  // Колонка сортировки исчезла (свернули группу) — возвращаемся к порядку уровней.
  useEffect(() => {
    if (sortKey === LEVEL_SORT_KEY) return;
    const visible = METRIC_COLUMNS.some((c) => c.id === sortKey) || placementColumns.some((c) => c.id === sortKey);
    if (!visible) {
      setSortKey(LEVEL_SORT_KEY);
      setSortDir('asc');
    }
  }, [sortKey, placementColumns]);

  const toggleGroup = useCallback((groupId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const handleSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir(key === LEVEL_SORT_KEY ? 'asc' : 'desc');
      }
    },
    [sortKey]
  );

  const controls = (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Макс. уровень кампании</Label>
        <CommitInput
          className="w-24 h-8"
          value={levelParam}
          min={1}
          max={LEVEL_LIMIT}
          fallback={DEFAULT_MAX_LEVEL}
          onCommit={(v) => setMaxLevel(v ?? DEFAULT_MAX_LEVEL)}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Макс. день жизни</Label>
        <CommitInput
          className="w-24 h-8"
          value={dayParam}
          min={0}
          max={DAY_LIMIT}
          placeholder="все"
          onCommit={(v) => setMaxDay(v)}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Значения</Label>
        <ToggleGroup type="single" size="sm" value={mode} onValueChange={(v) => v && setMode(v)}>
          <ToggleGroupItem value={MODE_IMPS} className="text-xs px-3">
            Показы
          </ToggleGroupItem>
          <ToggleGroupItem value={MODE_PCT} className="text-xs px-3">
            % игроков
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <label className="flex items-center gap-2 text-xs h-8">
        <Checkbox checked={!!gradient} onCheckedChange={(v) => setGradient(!!v)} />
        Градиент по столбцам
      </label>

      <label className="flex items-center gap-2 text-xs h-8">
        <Checkbox checked={!!orderByTotal} onCheckedChange={(v) => setOrderByTotal(!!v)} />
        Колонки по убыванию общего
      </label>

      {expandableIds.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => setExpanded(allExpanded ? new Set() : new Set(expandableIds))}
        >
          {allExpanded ? 'Свернуть группы' : 'Развернуть группы'}
        </Button>
      )}
    </div>
  );

  const horizon = dayParam === null ? 'весь срок жизни' : `D0–D${dayParam}`;
  const cohort = data?.cohortSize != null ? ` · когорта ${data.cohortSize.toLocaleString('ru-RU')}` : '';
  const clampNote = !data?.cohortClamped
    ? null
    : data.cohortWindow
      ? `регистрации обрезаны до ${data.cohortWindow.to}: более поздние ещё не прожили D${dayParam}`
      : `в окне нет регистраций, проживших D${dayParam}`;

  return (
    <BaseWidget
      {...widgetProps}
      title="Реклама по уровням"
      subtitle={[
        `Когорта по дате регистрации · уровень кампании в момент показа · ${horizon} · уровни 1–${levelParam}${cohort}`,
        clampNote
      ]
        .filter(Boolean)
        .join(' · ')}
      emptyMessage="Нет данных за выбранную когорту"
      skeletonRows={12}
      toolbar={controls}
    >
      <div className="space-y-4">
        <AdLevelsTable
          data={data}
          placementColumns={placementColumns}
          mode={mode}
          gradient={!!gradient}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          onToggleGroup={toggleGroup}
        />
        <p className="text-xs text-muted-foreground">
          Показ — сетевая реклама без домашней VIP. «Игроков» — дошли до уровня в выбранном горизонте; «% игроков» —
          доля игроков уровня, посмотревших рекламу в плейсменте. «Общее» — по уровням 1–{levelParam}. Платформа, гео и
          версии клиента режут события: «игроки уровня в версии X» — те, у кого на этом уровне были события в X. Клик по
          заголовку сортирует строки, стрелка у группы раскрывает её плейсменты.
        </p>
      </div>
    </BaseWidget>
  );
}

export default AdLevelsWidget;
